import React from 'react';
import { Container, Eyebrow, Section } from '../ui/Section';
import { ButtonExternal } from '../ui/Button';
import { Reveal, stagger } from '../motion/Reveal';
import { DoodleScatter } from '../brand/DoodleScatter';
import { DoodleUnderline } from '../brand/Doodles';
import { WelcomeWeekCard } from './WelcomeWeekCard';
import { welcomeWeek, welcomeWeekDays, welcomeWeekVisible } from '../../data/welcomeWeek';
import { externalLinks } from '../../data/site';

/**
 * Seasonal Welcome Week schedule. Renders nothing when `welcomeWeekVisible` is
 * false, so the whole block can be retired after August 28, 2026 without
 * touching the permanent weekly schedule.
 */
export function WelcomeWeek({
  headingId = 'welcome-week-heading',
  variant = 'home'
}: {
  headingId?: string;
  /** The Events page uses the dated framing; the homepage uses the invitation. */
  variant?: 'home' | 'events';
}) {
  if (!welcomeWeekVisible) return null;
  const isHome = variant === 'home';
  return <Section id="welcome-week" className="bcm-paper relative overflow-hidden py-16 sm:py-20" labelledBy={headingId} data-id="element-375" data-mp="17bhberwcw77g">
      <DoodleScatter tone="paper" seed={`welcome-week-${variant}`} data-id="element-376" data-mp="12bmnmx7vnxqm" />
      <Container className="relative" data-id="element-377" data-mp="1h1twcl8322e4">
        <Reveal data-id="element-378" data-mp="1h0r7sjy8gc1d">
          <div className="max-w-2xl" data-id="element-379" data-mp="1h3ddee1kchgy">
            <Eyebrow data-id="element-380" data-mp="1c1o0styozvqr">{isHome ? welcomeWeek.range : 'Fall 2026'}</Eyebrow>
            <h2 id={headingId} className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-5xl" data-id="element-381" data-mp="1c4jzwlma0qgs">
              {isHome ? welcomeWeek.heading : welcomeWeek.eventsHeading}
            </h2>
            <DoodleUnderline className="mt-3 h-3.5 w-44" color="var(--bcm-yellow)" data-id="element-382" data-mp="yc43i3nvrqou" />
            <p className="mt-5 font-sans text-[17px] leading-relaxed text-ink" data-id="element-383" data-mp="tawxoa5at1kt">
              {isHome ? welcomeWeek.intro : welcomeWeek.eventsIntro}
            </p>
          </div>
        </Reveal>

        {/* 3 + 2 on desktop, two columns on tablet, one per row on mobile. */}
        <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-6" data-id="element-384" data-mp="2buupnm003x43">
          {welcomeWeekDays.map((event, index) => <Reveal key={event.id} as="li" delay={stagger(index)} className={`h-full ${index < 3 ? 'lg:col-span-2' : 'lg:col-span-3'}`} data-id="element-385" data-mp="2grl5q9bt2spi" data-mpi={index}>
              <WelcomeWeekCard event={event} index={index} featured={index >= 3} data-id="element-386" data-mp="2qqtzb12re5bc" />
            </Reveal>)}
        </ul>

        <Reveal delay={0.1} data-id="element-387" data-mp="1t1qxk52flsbl">
          <div className="mt-10 rounded-[24px] border-2 border-brand-navy bg-brand-blue p-6 sm:p-8" data-id="element-388" data-mp="1y250x9t38e3a">
            <h3 className="font-display text-2xl font-extrabold leading-tight text-brand-navy sm:text-3xl" data-id="element-389" data-mp="231hpbyzlxt7z">
              Coming to BCM for the first time?
            </h3>
            <p className="mt-3 max-w-2xl font-sans text-[16px] leading-relaxed text-ink" data-id="element-390" data-mp="1ad3q5fws8quu">
              You don’t have to know anyone before you show up. Pick an event, bring a friend if you
              want, and come meet us!
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-391" data-mp="3sl5dggm2qmfy">
              <ButtonExternal href={externalLinks.getConnectedForm} data-id="element-392" data-mp="3oklhvhh5fqvx">Get Connected</ButtonExternal>
              <ButtonExternal href={externalLinks.instagram} variant="secondary" data-id="element-393" data-mp="3jkz496xes8ml">
                Follow on Instagram
              </ButtonExternal>
              <ButtonExternal href={externalLinks.discord} variant="secondary" data-id="element-394" data-mp="3eorvz7hjil1h">
                Join the Discord
              </ButtonExternal>
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>;
}